import { useSyncExternalStore } from "react";

/**
 * Whether this tab is on screen, so a poll can stop while nobody is watching.
 *
 * `useSyncExternalStore` rather than `useState` and an effect, because the flag is
 * external mutable state that can change between render and commit.
 *
 * TanStack Query's `refetchIntervalInBackground: false` default would also stop a timer,
 * but a library default is invisible in the component and untestable from it. A caller's
 * interval reads `visible &&` so the rule sits in the expression it governs.
 */
export function useDocumentVisible(): boolean {
  return useSyncExternalStore(subscribe, isVisible, alwaysVisible);
}

/**
 * Register a listener for tab visibility changes, for `useSyncExternalStore`.
 *
 * @internal
 * @param onStoreChange Called whenever the tab's visibility changes.
 * @returns A function that removes the listener.
 */
function subscribe(onStoreChange: () => void): () => void {
  document.addEventListener("visibilitychange", onStoreChange);
  return () => document.removeEventListener("visibilitychange", onStoreChange);
}

/**
 * Whether the tab is currently visible.
 *
 * @internal
 * @returns True when the document's visibility state is `visible`.
 */
function isVisible(): boolean {
  return document.visibilityState === "visible";
}

/**
 * The server snapshot. This app never server-renders, but the hook requires one, and
 * treating a non-browser render as hidden would leave a first paint whose poll never
 * starts.
 *
 * @internal
 */
function alwaysVisible(): boolean {
  return true;
}
